import { useState, useEffect } from "react";
import { Container, Col, Card, Spinner } from "react-bootstrap";
import { Button } from "@shopify/polaris";
import { AiOutlinePlus } from "react-icons/ai";
import { useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Redirect } from "@shopify/app-bridge/actions";
import createApp from "@shopify/app-bridge";

import axios from "axios";

import shopifyLogo from "../assets/images/shopify-logo.svg";
import msbcLogo from "../assets/images/msbcLogo.svg";

import {
  fetchStatus,
  onChangeMsbcConnected,
  setShowErrorModal,
  setShowSuccessModal,
} from "../store/slices/preferenceSlice";

import { getUrl } from "../helpers/url";

import { useCmsHook } from "../helpers/cmsHook";
import { CmsConstructor } from "../helpers/cmsConstructor";
import CmsEditAndSave from "../helpers/CmsEditAndSave";

const ConnectMsbc = () => {
  const { cmsInstance, setCmsInstance } = useCmsHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [disconnectLoading, setDisConnectLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { isMsbcConnected } = useSelector((state) => state.preference);

  const msbcConnected = useSelector(
    (state) => state?.data?.status?.creds?.msbc
  );

  const connectMsbcFromInstall = () => {
    try {
      setIsLoading(true);
      const shop = searchParams.get("shop");
      window.open(
        `${getUrl("REACT_APP_CONNECT_MSBC")}?shop=${shop}&path=${
          window.location.pathname.split("/")[1]
        }`,
        "_self"
      );
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const connectMsbcFromIframe = () => {
    try {
      setIsLoading(true);

      const config = {
        // The client ID provided for your application in the Partner Dashboard.
        apiKey: process.env.REACT_APP_SHOPIFY_CLIENT,
        // The host of the specific shop that's embedding your app. This value is provided by Shopify as a URL query parameter that's appended to your application URL when your app is loaded inside the Shopify admin.
        host: new URLSearchParams(location.search).get("host"),
        forceRedirect: true,
      };

      let app = createApp(config);

      const shop = searchParams.get("shop");

      const url = `${getUrl("REACT_APP_CONNECT_MSBC")}?shop=${shop}&path=${
        "/" + window.location.pathname.split("/")[1]
      }&${window.location.search.split("&")[2]}`;

      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.REMOTE, url);
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const disConnectMsbc = async () => {
    try {
      setDisConnectLoading(true);
      const shop = searchParams.get("shop");

      await axios.post(getUrl("REACT_APP_DISCONNECT_MSBC"), {
        shop,
      });
      dispatch(onChangeMsbcConnected({ isConnected: false }));

      dispatch(
        setShowSuccessModal({
          message:
            cmsInstance.sendData().cmsContent.notifications.authentication
              .successContent.disconnectEconomic.message,
        })
      );
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
    } finally {
      setDisConnectLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    //const tenantId = searchParams.get("tenantId");
    //const shop = searchParams.get("shop");
    if (msbcConnected) {
      dispatch(onChangeMsbcConnected({ isConnected: true }));

      dispatch(
        setShowSuccessModal({
          message:
            cmsInstance &&
            cmsInstance.sendData().cmsContent.notifications.authentication
              .successContent.connectEconomic.message,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msbcConnected]);

  useEffect(() => {
    if (window.location.pathname === "/connect-msbc") {
      const cmsInstance = async () => {
        const cmsObj = await CmsConstructor(dispatch);
        setCmsInstance(cmsObj);
      };
      cmsInstance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shop = searchParams.get("shop");
    if (shop) {
      dispatch(fetchStatus({ shop }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-container">
      {isLoading || !cmsInstance ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <div className="d-flex flex-row justify-content-center align-items-center">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </div>
      ) : (
        <>
          {window.location.pathname === "/connect-msbc" && (
            <div>
              <CmsEditAndSave />
            </div>
          )}
          <Container
            className="d-flex flex-row justify-content-center align-items-center"
            style={{
              minHeight: "100vh",
            }}
          >
            <Col lg={7} className="my-4">
              <Card>
                <div className="w-100 authentication-card d-flex flex-column align-items-center py-3">
                  {cmsInstance &&
                    cmsInstance.showRichText(
                      "cmsContent.authentication.heading",
                      "my-4"
                    )}
                  <div className="d-flex flex-row align-items-center justify-content-center my-4">
                    <div className="logo-container">
                      <Card.Img
                        src={shopifyLogo}
                        alt="shopify-logo"
                        className="logo"
                      />
                    </div>
                    <div>
                      <AiOutlinePlus size={17} className="plus-icon mx-3" />
                    </div>
                    <div className="logo-container">
                      <Card.Img
                        src={msbcLogo}
                        alt="msbc-logo"
                        className="logo"
                      />
                    </div>
                  </div>

                  {cmsInstance &&
                    cmsInstance.showRichText(
                      "cmsContent.authentication.description1",
                      "w-50 authentication-para"
                    )}

                  {isMsbcConnected &&
                    cmsInstance &&
                    cmsInstance.showRichText(
                      "cmsContent.authentication.description2.connected",
                      "text-center w-50"
                    )}
                  {!isMsbcConnected &&
                    cmsInstance &&
                    cmsInstance.showRichText(
                      "cmsContent.authentication.description2.disconnected",
                      "text-center w-50 my-2"
                    )}
                  <div className="d-flex my-3 d-flex flex-column">
                    <div>
                      <Button
                        onClick={() => {
                          searchParams.get("session")
                            ? connectMsbcFromIframe()
                            : connectMsbcFromInstall();
                        }}
                        className="connect-button"
                        disabled={isMsbcConnected}
                        primary
                        fullWidth
                      >
                        {isMsbcConnected
                          ? "Microsoft Business Central connected"
                          : "Connect Microsoft Business Central"}
                      </Button>
                    </div>
                    <div className="mt-2">
                      {isMsbcConnected && (
                        <Button
                          onClick={disConnectMsbc}
                          className="disconnect-button mt-2"
                          loading={disconnectLoading}
                          destructive
                        >
                          Disconnect Microsoft Business Central
                        </Button>
                      )}
                    </div>
                  </div>

                  {cmsInstance &&
                    cmsInstance.showRichText(
                      "cmsContent.authentication.description3",
                      "text-center w-50"
                    )}
                  <p className="w-50 text-center mt-3">
                    Here's the{" "}
                    <a href="/setupguide" target="_blank">
                      link
                    </a>{" "}
                    to the setup guide
                  </p>
                </div>
              </Card>
            </Col>
          </Container>
        </>
      )}
    </div>
  );
};

export default ConnectMsbc;
